// Browser local store, replacing the rusqlite database and file-based key
// storage in src-tauri/src/{lib,crypto}.rs. Everything lives in IndexedDB,
// scoped to this origin.
//
// Object stores:
//   kv         — keys + family metadata + sync cursor (out-of-line keys)
//                device_private_key/device_public_key/group_key: Uint8Array
//                auth_token/family_id/family_name/member_name: string
//                server_family_id/member_id/device_id/last_sequence: number
//   documents  — { docId, type, title, createdAt }  (keyPath docId)
//   updates    — { id++, docId, updateB64, createdAt } (index: docId) — encrypted at rest
//   outbox     — { id++, docId, docType, encryptedTitle, encryptedB64 } — offline push queue
//   deleted_docs — { docId, deletedAt } — tombstones; deleted docs stay deleted even if a
//                  racing edit for them arrives later (delete-wins).

const DB_NAME = "fam";
const DB_VERSION = 3;
const TODAY_DOC_ID = "today";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("kv")) db.createObjectStore("kv");
      if (!db.objectStoreNames.contains("documents")) {
        db.createObjectStore("documents", { keyPath: "docId" });
      }
      if (!db.objectStoreNames.contains("updates")) {
        const s = db.createObjectStore("updates", { keyPath: "id", autoIncrement: true });
        s.createIndex("docId", "docId", { unique: false });
      }
      if (!db.objectStoreNames.contains("outbox")) {
        db.createObjectStore("outbox", { keyPath: "id", autoIncrement: true });
      }
      // Phase 3: local cache of chat messages (encrypted at rest, keyed by server id)
      // so chat reads work offline and survive server-side delete-after-delivery GC.
      if (!db.objectStoreNames.contains("chat_messages")) {
        const c = db.createObjectStore("chat_messages", { keyPath: "id" });
        c.createIndex("channel", "channel", { unique: false });
        c.createIndex("createdAtMs", "createdAtMs", { unique: false });
      }
      // Tombstones for deleted documents — so a delete propagated through the sync journal
      // stays applied and a late/racing edit for the same doc can't resurrect it.
      if (!db.objectStoreNames.contains("deleted_docs")) {
        db.createObjectStore("deleted_docs", { keyPath: "docId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null; // Reset so the next caller can re-attempt openDb().
      reject(req.error);
    };
    req.onblocked = () => {
      dbPromise = null; // Reset so the next caller can re-attempt openDb().
      reject(new DOMException("IndexedDB open blocked", "AbortError"));
    };
  });
  return dbPromise;
}

function reqAsPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function run<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return reqAsPromise(fn(db.transaction(store, mode).objectStore(store)));
}

const nowSecs = (): number => Math.floor(Date.now() / 1000);

// --- Key/value (keys, credentials, family metadata, sync cursor) ---

export const kvGet = <T>(key: string): Promise<T | undefined> =>
  run<T>("kv", "readonly", (s) => s.get(key) as IDBRequest<T>);

export async function kvSet(key: string, value: unknown): Promise<void> {
  await run("kv", "readwrite", (s) => s.put(value, key));
}

export async function kvDelete(key: string): Promise<void> {
  await run("kv", "readwrite", (s) => s.delete(key));
}

export async function loadGroupKey(): Promise<Uint8Array> {
  const k = await kvGet<Uint8Array>("group_key");
  if (!k) throw new Error("No group key");
  return k;
}

export const loadAuthToken = async (): Promise<string | null> =>
  (await kvGet<string>("auth_token")) ?? null;

export const hasKeys = async (): Promise<boolean> =>
  (await kvGet<Uint8Array>("group_key")) != null;

export async function storeServerCredentials(
  authToken: string,
  serverFamilyId: number,
  memberId: number,
  deviceId: number,
): Promise<void> {
  await kvSet("auth_token", authToken);
  await kvSet("server_family_id", serverFamilyId);
  await kvSet("member_id", memberId);
  await kvSet("device_id", deviceId);
}

// --- Documents ---

export interface DocRow {
  docId: string;
  type: string;
  title: string;
  createdAt: number;
}

export interface DocMeta {
  docId: string;
  docType: string;
  title: string;
  createdAt: number;
}

const toDocMeta = (r: DocRow): DocMeta => ({
  docId: r.docId,
  docType: r.type,
  title: r.title,
  createdAt: r.createdAt,
});

export async function createDocument(docType: string, title: string): Promise<DocMeta> {
  const docId = `${docType}:${crypto.randomUUID()}`;
  const row: DocRow = { docId, type: docType, title, createdAt: nowSecs() };
  await run("documents", "readwrite", (s) => s.add(row));
  return toDocMeta(row);
}

/** INSERT OR IGNORE — used by sync/snapshot paths. Skips the singleton "today" doc and any
 *  doc that's been tombstoned (so a late edit can't recreate a deleted doc). */
export async function upsertDocumentIgnore(
  docId: string,
  type: string,
  title: string,
): Promise<void> {
  if (docId === TODAY_DOC_ID) return;
  if (await isDocumentDeleted(docId)) return;
  const existing = await run<DocRow | undefined>("documents", "readonly", (s) => s.get(docId));
  if (existing) return;
  await run("documents", "readwrite", (s) =>
    s.put({ docId, type, title, createdAt: nowSecs() } satisfies DocRow),
  );
}

/** True if the doc has been deleted (tombstoned) on this device. */
export async function isDocumentDeleted(docId: string): Promise<boolean> {
  const row = await run<{ docId: string } | undefined>("deleted_docs", "readonly", (s) => s.get(docId));
  return row != null;
}

/** Tombstone a doc and purge its local rows. Idempotent. */
export async function markDocumentDeleted(docId: string): Promise<void> {
  if (docId === TODAY_DOC_ID) return; // the singleton "today" doc is never deletable
  await run("deleted_docs", "readwrite", (s) => s.put({ docId, deletedAt: nowSecs() }));
  await deleteDocument(docId);
}

export async function listDocuments(docType?: string): Promise<DocMeta[]> {
  const rows = await run<DocRow[]>("documents", "readonly", (s) => s.getAll());
  return rows
    .filter((r) => docType === undefined || r.type === docType)
    .sort((a, b) => b.createdAt - a.createdAt)
    .map(toDocMeta);
}

export async function renameDocument(docId: string, title: string): Promise<void> {
  const row = await run<DocRow | undefined>("documents", "readonly", (s) => s.get(docId));
  if (!row) return;
  await run("documents", "readwrite", (s) => s.put({ ...row, title }));
}

export async function deleteDocument(docId: string): Promise<void> {
  const db = await openDb();
  const t = db.transaction(["documents", "updates"], "readwrite");
  t.objectStore("documents").delete(docId);
  const idx = t.objectStore("updates").index("docId");
  const keys = await reqAsPromise(idx.getAllKeys(IDBKeyRange.only(docId)));
  const updates = t.objectStore("updates");
  for (const key of keys) updates.delete(key as IDBValidKey);
  await new Promise<void>((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/** Mirrors lib.rs doc_metadata: returns [type, title], with the "today" special case. */
export async function docMetadata(docId: string): Promise<[string, string]> {
  if (docId === TODAY_DOC_ID) return ["today", "Today"];
  const row = await run<DocRow | undefined>("documents", "readonly", (s) => s.get(docId));
  return row ? [row.type, row.title] : ["unknown", "Untitled"];
}

// --- Yjs update journal (stores encrypted blobs, like yjs_updates) ---

export async function insertUpdate(docId: string, updateB64: string): Promise<void> {
  await run("updates", "readwrite", (s) => s.add({ docId, updateB64, createdAt: nowSecs() }));
}

interface UpdateRow {
  id: number;
  docId: string;
  updateB64: string;
}

/** Encrypted update blobs for a doc, in insertion (id-ascending) order. */
export async function loadUpdateBlobs(docId: string): Promise<string[]> {
  const rows = await run<UpdateRow[]>("updates", "readonly", (s) =>
    s.index("docId").getAll(IDBKeyRange.only(docId)),
  );
  return rows.map((r) => r.updateB64);
}

/** Encrypted update rows for a doc (with their ids), id-ascending. Used by compaction. */
export async function loadUpdateRows(docId: string): Promise<UpdateRow[]> {
  return run<UpdateRow[]>("updates", "readonly", (s) =>
    s.index("docId").getAll(IDBKeyRange.only(docId)),
  );
}

/**
 * Replace a doc's journal prefix (rows with id <= upToId) with a single snapshot blob,
 * atomically. Updates that arrived after the snapshot was built (id > upToId) are kept;
 * since Yjs applies updates order-independently, the snapshot + newer updates converge
 * to the same state as the full journal — so this only ever shrinks storage, never
 * loses data.
 */
export async function compactDocument(
  docId: string,
  snapshotB64: string,
  upToId: number,
): Promise<void> {
  const db = await openDb();
  const t = db.transaction("updates", "readwrite");
  const store = t.objectStore("updates");
  const keys = (await reqAsPromise(
    store.index("docId").getAllKeys(IDBKeyRange.only(docId)),
  )) as number[];
  for (const key of keys) {
    if (key <= upToId) store.delete(key);
  }
  store.add({ docId, updateB64: snapshotB64, createdAt: nowSecs() });
  await new Promise<void>((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

// --- Sync cursor ---

export const readSyncCursor = async (): Promise<number> =>
  (await kvGet<number>("last_sequence")) ?? 0;

export async function updateSyncCursor(seq: number): Promise<void> {
  await kvSet("last_sequence", seq);
}

// --- Outbox (offline push retry — not present in the Rust best-effort path) ---

export interface OutboxRow {
  id?: number;
  docId: string;
  docType: string;
  encryptedTitle: string;
  encryptedB64: string;
}

export async function enqueueOutbox(row: OutboxRow): Promise<void> {
  await run("outbox", "readwrite", (s) => s.add(row));
}

export const listOutbox = (): Promise<OutboxRow[]> =>
  run<OutboxRow[]>("outbox", "readonly", (s) => s.getAll());

export async function deleteOutbox(id: number): Promise<void> {
  await run("outbox", "readwrite", (s) => s.delete(id));
}

// --- Chat message cache (Phase 3: offline history + survives server GC) ---

export interface CachedChatRow {
  id: number;
  channel: string;
  senderMemberId: number;
  encryptedContent: string;
  createdAt: string; // ISO, for display
  createdAtMs: number; // for age-based local expiry
  // Sender name is resolved at render time from the decrypted roster (Phase 4b) —
  // it is no longer carried on the wire or cached.
}

export async function cacheChatMessages(rows: CachedChatRow[]): Promise<void> {
  if (rows.length === 0) return;
  const db = await openDb();
  const t = db.transaction("chat_messages", "readwrite");
  const s = t.objectStore("chat_messages");
  for (const r of rows) s.put(r); // upsert by id — idempotent dedupe
  await new Promise<void>((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/** Cached messages for a channel, id-ascending. */
export async function loadCachedChat(channel: string): Promise<CachedChatRow[]> {
  return run<CachedChatRow[]>("chat_messages", "readonly", (s) =>
    s.index("channel").getAll(IDBKeyRange.only(channel)),
  );
}

export const readChatCursor = async (): Promise<number> =>
  (await kvGet<number>("chat_cursor")) ?? 0;

export async function updateChatCursor(id: number): Promise<void> {
  await kvSet("chat_cursor", id);
}

/** Local-cache expiry: drop cached messages older than the cutoff (the client-side
 *  meaning of chat_retention_days). Server GC is independent (delete-after-delivery). */
export async function pruneChatBefore(cutoffMs: number): Promise<void> {
  const db = await openDb();
  const t = db.transaction("chat_messages", "readwrite");
  const s = t.objectStore("chat_messages");
  const keys = await reqAsPromise(
    s.index("createdAtMs").getAllKeys(IDBKeyRange.upperBound(cutoffMs, true)),
  );
  for (const key of keys) s.delete(key as IDBValidKey);
  await new Promise<void>((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

// --- Wipe everything (leave_family + clear_all_keys) ---

export async function clearAll(): Promise<void> {
  const db = await openDb();
  const stores = ["kv", "documents", "updates", "outbox", "chat_messages"];
  const t = db.transaction(stores, "readwrite");
  for (const name of stores) t.objectStore(name).clear();
  await new Promise<void>((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}
